const commands = [
  { cmd: '// message', desc: 'Post an internal note — visible to team only, never sent to client', badge: '🔒 NOTE' },
  { cmd: '//add @Name', desc: 'Add a team member to the current client thread', badge: 'THREAD' },
  { cmd: '//leave', desc: 'Remove yourself from the current thread', badge: 'THREAD' },
  { cmd: '//task <title>', desc: 'Create a task assigned to yourself in the current thread', badge: 'TASK' },
  { cmd: '//task <title> @Name @Name2', desc: 'Create separate tasks for each named assignee', badge: 'TASK' },
  { cmd: '//task <title> @Name due 2026-03-28', desc: 'Create a task with a due date (YYYY-MM-DD format)', badge: 'TASK' },
  { cmd: '@Name', desc: 'Open or continue a direct message thread with a team member', badge: 'DM' },
  { cmd: '@all message', desc: 'Post a broadcast message to the company Org thread', badge: 'ORG' },
];

const taskStatuses = [
  { status: 'Open', dot: '#B4B2A9', hollow: true, desc: 'New task — not yet started. Default state when created.' },
  { status: 'In Progress', dot: '#3B82F6', hollow: false, desc: 'Assignee has started working on the task.' },
  { status: 'Done', dot: '#1D9E75', hollow: false, desc: 'Task completed. Title shows with strikethrough.' },
];

const roles = [
  { name: 'Member', color: 'var(--blue)', desc: 'Full access — reply to clients, post notes, manage thread membership' },
  { name: 'Watcher', color: 'var(--v1)', desc: 'Read-only for client threads — can post internal notes, cannot reply to clients' },
  { name: 'Admin', color: '#DC2626', desc: 'All member permissions + manage users, view audit log, admin panel' },
];

const visibility = [
  { row: 'Client messages (inbound WhatsApp)', guest: '✓', member: '✓', watcher: '✓' },
  { row: 'Company replies to clients', guest: '✓', member: '✓', watcher: '✓' },
  { row: 'Internal notes (🔒)', guest: '✗', member: '✓', watcher: '✓' },
  { row: 'Other client accounts', guest: '✗', member: '✓', watcher: '✓' },
  { row: 'Internal team threads', guest: '✗', member: '✓', watcher: '✓' },
];

export default function HelpTab() {
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', background: 'var(--vbg)' }}>
      <h2 style={{ color: 'var(--v1)', fontSize: '1.1rem', fontWeight: 700, marginBottom: 4 }}>Command Reference</h2>
      <p style={{ color: 'var(--muted)', fontSize: '0.8rem', marginBottom: 20 }}>
        Type these in the message input to trigger actions.
      </p>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 40 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--vb)' }}>
            {['Command', 'What it does', 'Type'].map((h) => (
              <th key={h} style={{ textAlign: 'left', padding: '6px 12px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--xs)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {commands.map((c) => (
            <tr key={c.cmd} style={{ borderBottom: '1px solid rgba(0,0,0,.05)' }}>
              <td style={{ padding: '10px 12px' }}>
                <code style={{ background: 'var(--teal-50)', border: '1px solid var(--teal-200)', borderRadius: 3, padding: '2px 8px', fontSize: '0.8rem', color: 'var(--teal-700)', fontFamily: 'var(--font-mono)' }}>
                  {c.cmd}
                </code>
              </td>
              <td style={{ padding: '10px 12px', fontSize: '0.82rem', color: 'var(--muted)' }}>{c.desc}</td>
              <td style={{ padding: '10px 12px' }}>
                <span style={{
                  fontSize: '0.68rem', fontWeight: 700, padding: '2px 7px', borderRadius: 3,
                  background: c.badge === 'TASK' ? 'var(--amber-50)' : 'var(--teal-50)',
                  color: c.badge === 'TASK' ? '#92400E' : 'var(--v1)',
                }}>
                  {c.badge}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={{ color: 'var(--v1)', fontSize: '1.1rem', fontWeight: 700, marginBottom: 16 }}>Tasks</h2>
      <p style={{ color: 'var(--muted)', fontSize: '0.8rem', marginBottom: 14 }}>
        Tasks are created via <code style={{ color: '#92400E', background: 'var(--amber-50)', padding: '1px 6px', borderRadius: 3 }}>//task</code> commands in any chat. Client thread tasks are auto-linked to that client. Only the assignee can update a task's status or details.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 40 }}>
        {taskStatuses.map((t) => (
          <div key={t.status} style={{ background: 'var(--vs2)', border: '1px solid var(--vb)', borderRadius: 6, padding: '10px 16px', display: 'flex', gap: 14, alignItems: 'center', boxShadow: '0 1px 3px rgba(13,27,42,.05)' }}>
            <div style={{
              width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
              background: t.hollow ? 'transparent' : t.dot,
              border: t.hollow ? `2px solid ${t.dot}` : 'none',
              boxShadow: !t.hollow ? `0 0 6px ${t.dot}88` : 'none',
            }} />
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text)', minWidth: 90 }}>{t.status}</span>
            <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{t.desc}</span>
          </div>
        ))}
      </div>

      <h2 style={{ color: 'var(--v1)', fontSize: '1.1rem', fontWeight: 700, marginBottom: 16 }}>Roles</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 40 }}>
        {roles.map((r) => (
          <div key={r.name} style={{ background: 'var(--vs2)', border: '1px solid var(--vb)', borderRadius: 6, padding: '12px 16px', display: 'flex', gap: 14, alignItems: 'flex-start', boxShadow: '0 1px 3px rgba(13,27,42,.05)' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '3px 8px', borderRadius: 3, background: 'rgba(29,158,117,.1)', color: r.color, flexShrink: 0, border: `1px solid ${r.color}33` }}>
              {r.name.toUpperCase()}
            </span>
            <span style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>{r.desc}</span>
          </div>
        ))}
      </div>

      <h2 style={{ color: 'var(--v1)', fontSize: '1.1rem', fontWeight: 700, marginBottom: 16 }}>Visibility Matrix</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 40 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--vb)' }}>
            {['Content', 'Guest (client)', 'Member', 'Watcher'].map((h) => (
              <th key={h} style={{ textAlign: 'left', padding: '6px 12px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--xs)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibility.map((v) => (
            <tr key={v.row} style={{ borderBottom: '1px solid rgba(0,0,0,.05)' }}>
              <td style={{ padding: '9px 12px', fontSize: '0.82rem', color: 'var(--text)' }}>{v.row}</td>
              {[v.guest, v.member, v.watcher].map((val, i) => (
                <td key={i} style={{ padding: '9px 12px', fontSize: '0.88rem', fontWeight: 700, color: val === '✓' ? 'var(--teal-600)' : 'var(--red)' }}>
                  {val}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={{ color: 'var(--v1)', fontSize: '1.1rem', fontWeight: 700, marginBottom: 8 }}>Keyboard Shortcuts</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[
          ['Enter', 'Send message'],
          ['Shift+Enter', 'New line in message'],
          ['//', 'Switch to internal note mode'],
        ].map(([key, desc]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <kbd style={{ background: 'var(--vs2)', border: '1px solid var(--vb2)', borderRadius: 4, padding: '3px 8px', fontSize: '0.75rem', color: 'var(--teal-700)', fontFamily: 'var(--font-mono)', minWidth: 90, textAlign: 'center' }}>
              {key}
            </kbd>
            <span style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>{desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
